import apiClient from "../helpers/apiClient";
import { format, isValid, parseISO } from "date-fns";

export const subDepartmentService = {
  getSubDepartments,
  getSubDepartment,
  createSubDepartment,
  updateSubDepartment,
  deleteSubDepartment,
  setSubDepartmentStatus,
};

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function formatSubDepartmentDate(value) {
  if (value === null || value === undefined || value === "") return "-";

  const date =
    value instanceof Date
      ? value
      : typeof value === "string"
        ? parseISO(value)
        : new Date(value);

  return isValid(date) ? format(date, "dd MMM yyyy") : "-";
}

function findSubDepartmentPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const wrapped = payload.Sub_Department_info ?? payload.sub_department_info;
  if (Array.isArray(wrapped)) return wrapped;
  if (wrapped && typeof wrapped === "object") return [wrapped];
  if (payload.sub_department_id !== undefined) return [payload];
  return [];
}

function mapSubDepartment(raw = {}) {
  return {
    id: displayValue(raw.sub_department_id),
    name: displayValue(raw.sub_department_name),
    departmentId: displayValue(raw.department_id),
    departmentName: displayValue(raw.department_name),
    statusId: displayValue(raw.sub_department_status_id),
    statusName: displayValue(raw.sub_department_status_name),
    createdBy: displayValue(raw.encoded_by_name),
    createdAt: formatSubDepartmentDate(raw.encoded_date),
    createdAtRaw: raw.encoded_date ?? "",
    lastChangedBy: displayValue(raw.last_changed_by_name),
    lastChangedAt: formatSubDepartmentDate(raw.last_changed_date),
  };
}

function getErrorMessage(error) {
  return error.response?.data?.msg || "Something went wrong";
}

function extractWriteResult(response) {
  const data = response?.data;
  const messageObject = Array.isArray(data)
    ? data[0]
    : Array.isArray(data?.result_status)
      ? data.result_status[0]
      : data;

  return {
    message:
      messageObject?.Message_Text ||
      messageObject?.message_text ||
      "Saved successfully.",
    refNo: messageObject?.Message_Ref_No ?? messageObject?.message_ref_no ?? null,
  };
}

async function handleRequest(request) {
  try {
    return await request();
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

async function handleWriteRequest(request) {
  try {
    const response = await request();
    return extractWriteResult(response);
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

function getSubDepartments() {
  return handleRequest(async () => {
    const response = await apiClient.get("/hr/sub-departments");
    const data = response?.data;
    return {
      subDepartments: findSubDepartmentPayload(data).map(mapSubDepartment),
      departmentOptions: Array.isArray(data?.department_info)
        ? data.department_info
        : [],
    };
  });
}

function getSubDepartment(id) {
  return handleRequest(async () => {
    const response = await apiClient.get(`/hr/sub-departments/${id}`);
    const [subDepartment] = findSubDepartmentPayload(response?.data);
    return subDepartment ? mapSubDepartment(subDepartment) : null;
  });
}

function createSubDepartment(name, departmentId) {
  return handleWriteRequest(() =>
    apiClient.post("/hr/sub-departments", {
      sub_department_name: name,
      department_id: String(departmentId),
    })
  );
}

function updateSubDepartment(id, name, statusId, departmentId) {
  return handleWriteRequest(() =>
    apiClient.put(`/hr/sub-departments/${id}`, {
      sub_department_name: name,
      sub_department_status_id: String(statusId),
      department_id: String(departmentId),
    })
  );
}

function deleteSubDepartment(id) {
  return handleWriteRequest(() => apiClient.delete(`/hr/sub-departments/${id}`));
}

function setSubDepartmentStatus(id, statusId) {
  return handleWriteRequest(() =>
    apiClient.patch(`/hr/sub-departments/${id}/status`, {
      sub_department_status_id: String(statusId),
    })
  );
}
